import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

import { useClaim } from '~/components/ClaimContainer';
import { FsButton } from '~/components/core/FsWrappers';
import { PERMISSION_ACTIONS, PERMISSION_VERBS } from '~/components/core/Permissions/PermissionUtils';
import { RestrictedPermissions } from '~/components/core/Permissions/RestrictedPermissions';
import SimpleSearchField from '~/components/core/SimpleSearchField';
import { useCms } from '~/components/hooks/useCms';
import { useIncidentConfiguration } from '~/components/hooks/useIncidentConfiguration';
import { useLob } from '~/components/hooks/useLob';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import { useLobConfiguredFields } from '~/components/hooks/useLobConfiguredFields';
import AddIcon from '~/components/icons/AddIcon';
import useOrganization from '~/components/OrganizationContext';
import useDebouncedValue from '~/hooks/useDebouncedValue';
import type { CoveredObjectsResponseCoveredObjectsItem as CoveredObjectRow } from '~/server_shared/openapi/data-contracts';
import type { AutoClaim, GeneralClaimForFe } from '~/server_shared/swagger/data-contracts';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isClaimPolicyManuallyFilled, isFeatureEnabled, reportAxiosError } from '~/Utils';

import UnlistedVehicleCard from '../CoveredEntity/AutoSpecific/UnlistedVehicleCard';
import CoveredEntityCard from '../CoveredEntity/CoveredEntityCard';
import CoveredObjectTypeSelector from '../CoveredEntity/CoveredObjectTypeSelector/CoveredObjectTypeSelector';
import EditCoveredEntityDialog from '../CoveredEntity/EditCoveredEntityDialog/EditCoveredEntityDialog';
import FirstPartyPedestrianWrapper from '../CoveredEntity/FirstPartyPedestrianWrapper';
import UnlistedCoveredObjectCard from '../CoveredEntity/UnlistedCoveredObjectCard/UnlistedCoveredObjectCard';
import { getAutoFieldsConfig } from '../GenericPolicyPage';
// Re-use the shared hook's type (desc/icon optional) for prop typing within
// this file. The CoveredObjectTypeSelector requires a stricter shape and we
// adapt at that one call site below.
import type { AvailableCoveredObjectType as AvailableType } from '../useAddCoveredObjectFlow';
import { useAddCoveredObjectFlow } from '../useAddCoveredObjectFlow';

import {
  COVERED_OBJECTS_CARDS_THRESHOLD,
  COVERED_OBJECTS_SEARCH_DEBOUNCE_MS,
  COVERED_OBJECTS_SEARCH_MIN_CHARS,
  COVERED_OBJECTS_TABLE_DEFAULT_FIELD_COLUMNS,
} from './constants';
import CoveredObjectsTable from './CoveredObjectsTable';
import CoveredObjectViewDialog from './CoveredObjectViewDialog';
import { useCoveredObjectsQuery } from './useCoveredObjectsQuery';

type FieldConfigItem = { id: string; desc?: string; type?: string; value?: unknown; [key: string]: unknown };

type FieldsConfig = {
  defaultFields?: FieldConfigItem[];
  configuredFields?: FieldConfigItem[];
  coveredObjectTypeLabel?: string;
  Icon?: React.ComponentType;
  icon?: React.ComponentType;
};

const useTypeFieldsConfig = (
  typeId: string,
  isAutoClaim: boolean,
  coveredObject: CoveredObjectRow | null = null
): FieldsConfig => {
  // Hooks must be called unconditionally; both branches always run, the
  // unused side is cheap because the hook short-circuits on a null arg.
  // When `coveredObject` is passed, the returned defaultFields/configuredFields
  // come back with values populated from that row (V1 contract).
  const { incidentConfiguration } = useIncidentConfiguration();
  // @ts-expect-error TODO: migrate OrganizationContext.jsx → .tsx. The hook's declared return is `{}`; at runtime it includes vehicleTypesDict.
  const { vehicleTypesDict } = useOrganization();
  const lobConfigArg = typeId && !isAutoClaim ? (coveredObject ?? { type: typeId }) : null;
  // @ts-expect-error TODO: widen useLobConfiguredFields' TS signature to accept the runtime config object (and migrate the file to TS).
  const lobConfig: FieldsConfig = useLobConfiguredFields(lobConfigArg, false);

  if (typeId && isAutoClaim) {
    // @ts-expect-error TODO: type getAutoFieldsConfig (migrate GenericPolicyPage.jsx → .tsx).
    return getAutoFieldsConfig({ incidentConfiguration, coveredObject, vehicleTypesDict });
  }
  return lobConfig;
};

const renderIcon = (fieldsConfig: FieldsConfig | null | undefined): React.ReactElement | null => {
  // useLobConfiguredFields returns `Icon` (component class); getAutoFieldsConfig
  // returns `icon`. Either way the EditCoveredEntityDialog expects a rendered
  // ReactElement, not a component reference.
  const IconComponent = fieldsConfig?.Icon ?? fieldsConfig?.icon;
  return IconComponent ? <IconComponent /> : null;
};

interface CoveredObjectInlineCardProps {
  row: CoveredObjectRow;
  typeId: string;
  isAutoClaim: boolean;
}

const CoveredObjectInlineCard: React.FC<CoveredObjectInlineCardProps> = ({ row, typeId, isAutoClaim }) => {
  const fieldsConfig = useTypeFieldsConfig(typeId, isAutoClaim, row);
  return (
    <CoveredEntityCard
      icon={renderIcon(fieldsConfig)}
      coveredObjectTypeLabel={fieldsConfig?.coveredObjectTypeLabel ?? ''}
      defaultFields={fieldsConfig?.defaultFields ?? []}
      configuredFields={fieldsConfig?.configuredFields ?? []}
      coveredObjectIdForPatchEndpoints={row.id}
      coveredObject={row}
      isFirstParty={Boolean(row.is_first_party)}
      fetchCoveredEntityEndpoint={`/api/v1/policies/${row.policy_id}/covered_entities/${row.covered_entity_id}`}
    />
  );
};

interface ConnectedCoveredObjectsTableProps {
  typeId: string;
  typeLabel: string;
  organizationId: number;
  claimId: number;
  policyId?: number;
  search: string;
  isManualPolicy: boolean;
  isAutoClaim: boolean;
  onDelete?: (row: CoveredObjectRow) => void;
  onMutated: (typeId: string) => void;
  refreshToken: number;
  renderAsCards: boolean;
  // Reports the unfiltered totalCount up to the tab so the showSearch + global
  // cards-vs-table decisions can be driven by stable numbers (not the
  // user-filtered current count). Fires with `null` while loading or
  // unmounted so the parent's allTypesReady gate can distinguish in-flight
  // from "loaded with 0".
  onUnfilteredCount: (typeId: string, count: number | null) => void;
}

const ConnectedCoveredObjectsTable: React.FC<ConnectedCoveredObjectsTableProps> = ({
  typeId,
  typeLabel,
  organizationId,
  claimId,
  policyId,
  search,
  isManualPolicy,
  isAutoClaim,
  onDelete,
  onMutated,
  refreshToken,
  renderAsCards,
  onUnfilteredCount,
}) => {
  const { items, totalCount, appliedSearch, page, perPage, isLoading, isError, setPage, setPerPage } =
    useCoveredObjectsQuery({
      organizationId,
      claimId,
      type: typeId,
      search,
      refreshToken,
    });

  // Report the unfiltered total up so the parent can drive the showSearch
  // flag and the global cards-vs-table decision. Local copy feeds the table
  // header's "X of Y" while filtering.
  //
  // Gate on BOTH `search` and `appliedSearch` being empty:
  //   - `search` to skip while the user is actively typing (filtered results
  //     are not "unfiltered totals").
  //   - `appliedSearch` to also skip during the brief window where `search`
  //     has flipped to '' (e.g. dropped below MIN_CHARS) but the last
  //     successful fetch was filtered — otherwise we'd report the filtered
  //     totalCount as "unfiltered" and momentarily collapse the parent's
  //     showSearch flag, remounting the search input and eating keystrokes.
  //
  // While the gate is open and we're loading, report null so the parent's
  // allTypesReady gate sees "in flight" instead of a stale count from the
  // previous fetch / previous claim.
  const [lastUnfilteredCount, setLastUnfilteredCount] = useState<number | null>(null);
  useEffect(() => {
    if (search !== '' || appliedSearch !== '') {
      // Filtered: re-push the last-known unfiltered count so the parent's
      // allTypesReady gate doesn't get stranded on a null from an unfiltered
      // refetch the user started searching during. First-load case (no
      // unfiltered total yet) keeps null — the correct "not ready" signal.
      if (lastUnfilteredCount !== null) {
        onUnfilteredCount?.(typeId, lastUnfilteredCount);
      }
      return;
    }
    if (isLoading) {
      onUnfilteredCount?.(typeId, null);
      return;
    }
    setLastUnfilteredCount(totalCount);
    onUnfilteredCount?.(typeId, totalCount);
  }, [onUnfilteredCount, typeId, totalCount, search, appliedSearch, isLoading, lastUnfilteredCount]);
  // Drop the entry from the tab's unfilteredCounts map on unmount so a
  // LOB-switch doesn't leak stale counts. Use null (not 0) so the parent's
  // allTypesReady predicate treats unmounted types as "not ready" — same
  // semantic as in-flight loads.
  useEffect(() => () => onUnfilteredCount?.(typeId, null), [onUnfilteredCount, typeId]);

  // Schema-only field config (no row values) — used to drive table column
  // headers. The row payload from T3 already mirrors v1's covered-object
  // shape, so the per-row values are pulled directly off the row in the
  // table renderer / view dialog (no BE-side projection needed).
  const fieldsConfig = useTypeFieldsConfig(typeId, isAutoClaim);

  // Schema-with-values for the *currently viewed* row — feeds the view
  // dialog. For auto: getAutoFieldsConfig is a pure function so we can
  // call it with the viewRow inline; for non-auto: useLobConfiguredFields
  // is a hook, but we call it unconditionally (with null when no row) at
  // the top of the component below.
  const [viewRow, setViewRow] = useState<CoveredObjectRow | null>(null);
  const viewRowFieldsConfig = useTypeFieldsConfig(typeId, isAutoClaim, viewRow);

  // Close the view dialog if the row it's bound to disappeared from the
  // listing — typically because the user just deleted that row via the
  // table's inline trash icon while the modal was open for the same row.
  // Without this, the modal stays mounted on a row that no longer exists
  // and the next fetch the inner card fires would 404. Gate on !isLoading
  // so the hook's pre-fetch `setItems([])` clear doesn't briefly close the
  // dialog mid-refetch.
  useEffect(() => {
    if (!isLoading && viewRow && !items.some((item) => item.id === viewRow.id)) {
      setViewRow(null);
    }
  }, [items, viewRow, isLoading]);

  // When the user is actively searching we keep the empty card visible so
  // the "no results" feedback is obvious; otherwise we hide types that have
  // zero rows to avoid cluttering the page. A request that failed before any
  // successful response (totalCount === 0 + isError) still needs to fall
  // through so the table can render its error card.
  //
  // Gate on BOTH `search` (the next fetch's filter) and `appliedSearch` (what
  // the current rows reflect): otherwise shortening the input below MIN_CHARS
  // — or clearing whitespace-only text — flips the UI to "not searching"
  // before the unfiltered refetch lands, even though the visible rows are
  // still the filtered subset.
  const isSearchActive = (search ?? '').trim() !== '' || (appliedSearch ?? '').trim() !== '';
  if (totalCount === 0 && !isLoading && !isError && !isSearchActive) {
    return null;
  }

  // Below the threshold (applied to the TOTAL across all types, owned by the
  // parent) the table is overkill — fall back to the v1 cards in a 2-col
  // grid. Each card fetches its own per-entity data (same as v1). While
  // searching we always render the table — the user is in a filtering
  // workflow and the "X of Y" + pagination only live in the table header.
  const shouldRenderAsCards = !isSearchActive && renderAsCards && totalCount > 0 && !isLoading && !isError;

  return (
    <>
      {shouldRenderAsCards ? (
        <div className="grid items-start gap-12 min-[940px]:grid-cols-2">
          {items.map((row) => (
            <CoveredObjectInlineCard key={row.id} row={row} typeId={typeId} isAutoClaim={isAutoClaim} />
          ))}
        </div>
      ) : (
        <CoveredObjectsTable
          typeLabel={typeLabel}
          items={items}
          totalCount={totalCount}
          unfilteredCount={lastUnfilteredCount}
          page={page}
          perPage={perPage}
          isLoading={isLoading}
          isError={isError}
          isManualPolicy={isManualPolicy}
          isAutoClaim={isAutoClaim}
          isSearchActive={isSearchActive}
          defaultFieldConfigs={(fieldsConfig.defaultFields ?? []).slice(0, COVERED_OBJECTS_TABLE_DEFAULT_FIELD_COLUMNS)}
          onPageChange={setPage}
          onPerPageChange={setPerPage}
          onView={setViewRow}
          onDelete={onDelete}
        />
      )}
      {viewRow && (
        <CoveredObjectViewDialog
          row={viewRow}
          policyId={policyId}
          icon={renderIcon(viewRowFieldsConfig)}
          coveredObjectTypeLabel={viewRowFieldsConfig?.coveredObjectTypeLabel ?? typeLabel}
          defaultFields={viewRowFieldsConfig?.defaultFields ?? []}
          configuredFields={viewRowFieldsConfig?.configuredFields ?? []}
          onEdited={setViewRow}
          onMutated={() => onMutated?.(typeId)}
          onClose={() => setViewRow(null)}
        />
      )}
    </>
  );
};

interface AddCoveredObjectDialogProps {
  addType: string;
  isAutoClaim: boolean;
  availableTypes: AvailableType[];
  isSaving: boolean;
  onSave: (values: Record<string, unknown>) => Promise<void>;
  onClose: () => void;
}

const AddCoveredObjectDialog: React.FC<AddCoveredObjectDialogProps> = ({
  addType,
  isAutoClaim,
  availableTypes,
  isSaving,
  onSave,
  onClose,
}) => {
  const fieldsConfig = useTypeFieldsConfig(addType, isAutoClaim);
  const typeLabel =
    availableTypes.find((t) => t.id === addType)?.desc ?? fieldsConfig.coveredObjectTypeLabel ?? 'Covered Object';

  return (
    <EditCoveredEntityDialog
      isUpdate={false}
      onClose={onClose}
      icon={renderIcon(fieldsConfig)}
      coveredObjectTypeLabel={typeLabel}
      onSaveClick={onSave}
      isSaving={isSaving}
      configuredFields={fieldsConfig.configuredFields ?? []}
      defaultFields={fieldsConfig.defaultFields ?? []}
    />
  );
};

const CoveredObjectsTab: React.FC = () => {
  const { claim, onAsyncClaimUpdate } = useClaim({ claimRequired: true });
  const { userOrganization } = useCms();
  const { lob } = useLob();
  const { lobConfigurationsDict = {} } = useLobConfiguration();

  const isAutoClaim = lob === 'auto_claim';
  // Safe inside the isAutoClaim guard below; only those branches read the AutoClaim-only fields.
  const autoClaim = claim as AutoClaim;
  // unlisted_covered_objects is served only on the general claim payload, so it is
  // absent (not empty) on an auto claim and the cards below render for neither.
  const generalClaim = claim as GeneralClaimForFe;
  const isManualPolicy = isClaimPolicyManuallyFilled(claim);
  const policyPrefix = isManualPolicy ? '' : 'api_';
  const isConfigurableCoveragesEnabled = isFeatureEnabled(
    userOrganization,
    CONFIGURATION_FEATURES_NAMES.CONFIGURABLE_COVERAGES
  );

  // Debounce the search input so we don't fire a request on every keystroke.
  // Trim afterwards so whitespace-only input behaves like no input: the BE
  // doesn't see a no-op ILIKE filter and `isSearchActive` collapses to false.
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, COVERED_OBJECTS_SEARCH_DEBOUNCE_MS);
  const trimmedSearch = debouncedSearch.trim();
  // Only treat input as an active search at >= MIN_CHARS so 1-2 char prefixes
  // (which would ILIKE against most rows) don't hit the BE.
  const search = trimmedSearch.length >= COVERED_OBJECTS_SEARCH_MIN_CHARS ? trimmedSearch : '';

  // Per-type refresh trigger. Each ConnectedCoveredObjectsTable consumes its
  // token via useCoveredObjectsQuery's deps array, so bumping the entry for
  // a given typeId causes only that table to refetch. The dialog and the
  // table's row-delete path both call bumpRefresh(typeId) on mutation.
  // (Replaces the prior refreshFnsRef PubSub which had a stale-closure
  // footgun and never cleaned up entries on unmount.)
  const [refreshTokens, setRefreshTokens] = useState<Record<string, number>>({});
  const bumpRefresh = useCallback(
    (typeId: string) => setRefreshTokens((prev) => ({ ...prev, [typeId]: (prev[typeId] ?? 0) + 1 })),
    []
  );

  const {
    availableTypes,
    addType,
    showTypeSelector,
    isCreating,
    handleAdd,
    handleAddTypeSelected,
    handleCreate,
    closeTypeSelector,
    closeAddDialog,
  } = useAddCoveredObjectFlow({
    isAutoClaim,
    rawAvailableTypes: lobConfigurationsDict[lob]?.covered_objects,
    claimId: claim.id,
    isManualPolicy,
    onAsyncClaimUpdate,
    onCreated: bumpRefresh,
  });

  // Per-type *unfiltered* totals bubbled up from each ConnectedCoveredObjectsTable.
  // Two decisions key off these:
  //   - showSearch: only when at least one type would render as a table
  //     (searching across cards-only types adds no value).
  //   - renderAsCards globally: applied to the total across all types so a
  //     claim with 3 vehicles + 3 properties renders BOTH as tables (6 > 4)
  //     instead of each type independently falling under the threshold.
  // Using *unfiltered* counts (gated in the child on both search props being
  // empty) keeps the decisions stable while the user types. The
  // `allTypesReady` gate prevents a race during the initial multi-type load
  // and during mutation refetches: each child reports null while its fetch
  // is in flight, so the sum is only computed once every available type has
  // a settled non-null count.
  const [unfilteredCounts, setUnfilteredCounts] = useState<Record<string, number | null>>({});
  const handleUnfilteredCount = useCallback((typeId: string, count: number | null) => {
    setUnfilteredCounts((prev) => (prev[typeId] === count ? prev : { ...prev, [typeId]: count }));
  }, []);
  const allTypesReady =
    availableTypes.length > 0 &&
    availableTypes.every((t) => unfilteredCounts[t.id] !== null && unfilteredCounts[t.id] !== undefined);
  const totalAcrossTypes = allTypesReady
    ? availableTypes.reduce((sum, t) => sum + (unfilteredCounts[t.id] ?? 0), 0)
    : 0;
  const renderAsCards = allTypesReady && totalAcrossTypes > 0 && totalAcrossTypes < COVERED_OBJECTS_CARDS_THRESHOLD;
  // Search input is meaningful whenever ANY type would render as a table —
  // i.e. when the total sum crosses the threshold. The earlier "any single
  // type ≥ threshold" check was wrong: multi-type claims that each stay
  // under the threshold but sum above it would render tables WITHOUT search.
  const showSearch = allTypesReady && totalAcrossTypes >= COVERED_OBJECTS_CARDS_THRESHOLD;

  const handleDelete = async (row: CoveredObjectRow, typeId: string) => {
    const url = `/api/v1/claims/${claim.id}/${policyPrefix}policy/covered_objects/${row.id}`;
    try {
      // A claim-owned object goes back to unlisted rather than being deleted, so its
      // coverages and exposures survive.
      await (row.is_claim_owned ? axios.patch(`${url}/set_unlisted`) : axios.delete(url));
      await onAsyncClaimUpdate();
      bumpRefresh(typeId);
    } catch (error) {
      reportAxiosError(error);
    }
  };

  // Page-level actions (search + Add Covered Object) render at the top of the
  // tab body, on their own row above the per-type tables.
  const headerActions = availableTypes.length > 0 && (
    <>
      {showSearch && (
        <div className="relative w-[280px]" data-testid="covered-objects-search">
          <SimpleSearchField placeholder="Search covered objects" renderClearButton onChange={setSearchInput} />
          {searchInput.trim().length > 0 && searchInput.trim().length < COVERED_OBJECTS_SEARCH_MIN_CHARS && (
            <div
              className="absolute left-0 top-full mt-4 text-xs text-slate-700"
              data-testid="covered-objects-search-helper"
            >
              Type at least {COVERED_OBJECTS_SEARCH_MIN_CHARS} characters to search.
            </div>
          )}
        </div>
      )}
      <RestrictedPermissions
        action={isManualPolicy ? PERMISSION_ACTIONS.MANUAL_POLICY : PERMISSION_ACTIONS.API_POLICY}
        verb={PERMISSION_VERBS.WRITE}
      >
        <FsButton color="primary" onClick={handleAdd} data-testid="add-covered-object-button">
          <AddIcon className="mr-8" iconColor="currentColor" />
          Add Covered Object
        </FsButton>
      </RestrictedPermissions>
    </>
  );

  return (
    <div data-testid="covered-objects-tab">
      {headerActions && (
        <div className="mt-12 flex w-full items-center justify-end gap-12 px-[10px]">{headerActions}</div>
      )}

      {isAutoClaim && Boolean(autoClaim.unlisted_covered_vehicle) && (
        <div className="m-[10px]">
          <UnlistedVehicleCard
            claim={claim}
            onClaimUpdate={onAsyncClaimUpdate}
            coveredVehicle={autoClaim.unlisted_covered_vehicle as object}
            isRental={Boolean(autoClaim.is_unlisted_vehicle_rental)}
          />
        </div>
      )}

      {generalClaim.unlisted_covered_objects?.map((unlistedCoveredObject) => (
        <div key={unlistedCoveredObject.id} className="m-[10px]">
          <UnlistedCoveredObjectCard
            claim={claim}
            coveredObject={unlistedCoveredObject}
            onClaimUpdate={onAsyncClaimUpdate}
          />
        </div>
      ))}

      {isAutoClaim && isConfigurableCoveragesEnabled && Boolean(autoClaim.pedestrian_covered_entity_id) && (
        <div className="m-[10px]">
          <FirstPartyPedestrianWrapper />
        </div>
      )}

      {availableTypes.map((type) => (
        <div key={type.id} className="m-[10px]">
          <ConnectedCoveredObjectsTable
            typeId={type.id}
            typeLabel={type.desc ?? type.id}
            organizationId={userOrganization.id}
            claimId={claim.id}
            policyId={claim?.policy?.id}
            search={search}
            isManualPolicy={isManualPolicy}
            isAutoClaim={isAutoClaim}
            refreshToken={refreshTokens[type.id] ?? 0}
            onMutated={bumpRefresh}
            renderAsCards={renderAsCards}
            onUnfilteredCount={handleUnfilteredCount}
            onDelete={(row) => handleDelete(row, type.id)}
          />
        </div>
      ))}

      {showTypeSelector && (
        <CoveredObjectTypeSelector
          open={showTypeSelector}
          availableTypes={availableTypes.map((t) => ({ id: t.id, desc: t.desc ?? t.id, icon: t.icon ?? '' }))}
          onTypeSelected={handleAddTypeSelected}
          onClose={closeTypeSelector}
        />
      )}

      {addType && (
        <AddCoveredObjectDialog
          addType={addType}
          isAutoClaim={isAutoClaim}
          availableTypes={availableTypes}
          isSaving={isCreating}
          onSave={handleCreate}
          onClose={closeAddDialog}
        />
      )}
    </div>
  );
};

export default CoveredObjectsTab;
